/** Shared filter type vocabulary — frontend camelCase ↔ API snake_case. */

export type FilterOperationApi =
  | "eq"
  | "ne"
  | "gt"
  | "lt"
  | "ge"
  | "le"
  | "contains"
  | "not_contains"
  | "in"
  | "not_in"
  | "between"
  | "outside_range"
  | "is_null"
  | "is_not_null"
  | "starts_with"
  | "ends_with";

export type FilterType =
  | "equals"
  | "notEquals"
  | "contains"
  | "notContains"
  | "greaterThan"
  | "lessThan"
  | "greaterEqual"
  | "lessEqual"
  | "range"
  | "outsideRange"
  | "in"
  | "notIn"
  | "isNull"
  | "isNotNull"
  | "startsWith"
  | "endsWith";

export const API_TO_FRONTEND_TYPE: Record<string, FilterType> = {
  eq: "equals",
  ne: "notEquals",
  contains: "contains",
  not_contains: "notContains",
  gt: "greaterThan",
  lt: "lessThan",
  ge: "greaterEqual",
  le: "lessEqual",
  between: "range",
  outside_range: "outsideRange",
  in: "in",
  not_in: "notIn",
  is_null: "isNull",
  is_not_null: "isNotNull",
  starts_with: "startsWith",
  ends_with: "endsWith",
};

export const FRONTEND_TO_API_TYPE: Record<FilterType, FilterOperationApi> = {
  equals: "eq",
  notEquals: "ne",
  contains: "contains",
  notContains: "not_contains",
  greaterThan: "gt",
  lessThan: "lt",
  greaterEqual: "ge",
  lessEqual: "le",
  range: "between",
  outsideRange: "outside_range",
  in: "in",
  notIn: "not_in",
  isNull: "is_null",
  isNotNull: "is_not_null",
  startsWith: "starts_with",
  endsWith: "ends_with",
};

export function apiOperationToFrontendType(operation: string): FilterType | string {
  return API_TO_FRONTEND_TYPE[operation] ?? operation;
}

export function frontendTypeToApiOperation(type: string): string {
  return FRONTEND_TO_API_TYPE[type as FilterType] ?? type;
}

/** Compact operator badge shown inside filter pills. */
export interface OperatorBadge {
  /** Short glyph or symbol for the pill */
  badge: string;
  /** Accessible / tooltip label */
  label: string;
}

export const OPERATOR_BADGES: Record<FilterType, OperatorBadge> = {
  equals: { badge: "=", label: "Equals" },
  notEquals: { badge: "≠", label: "Not equals" },
  contains: { badge: "~", label: "Contains" },
  notContains: { badge: "!~", label: "Does not contain" },
  greaterThan: { badge: ">", label: "Greater than" },
  lessThan: { badge: "<", label: "Less than" },
  greaterEqual: { badge: "≥", label: "Greater or equal" },
  lessEqual: { badge: "≤", label: "Less or equal" },
  range: { badge: "↔", label: "Between" },
  outsideRange: { badge: "⊄", label: "Outside range" },
  in: { badge: "∈", label: "In" },
  notIn: { badge: "∉", label: "Not in" },
  startsWith: { badge: "^", label: "Starts with" },
  endsWith: { badge: "$", label: "Ends with" },
  isNull: { badge: "∅", label: "Is empty" },
  isNotNull: { badge: "•", label: "Has value" },
};

export function getOperatorBadge(type: string): OperatorBadge {
  return (
    OPERATOR_BADGES[type as FilterType] ?? {
      badge: type,
      label: type,
    }
  );
}
